import React, { useState } from 'react';
import {
    Box, TextField, Button, Typography, Paper, useTheme, CircularProgress, Alert,
    Table, TableBody, TableCell, TableContainer, TableRow, TableHead, Dialog, DialogTitle,
    DialogContent, DialogActions
} from '@mui/material';
import { tableCellClasses } from '@mui/material/TableCell';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import Lottie from 'lottie-react';
import corazonAnim from '../../../img/corazon.json';
import logoseparador from '../../../img/logoseparador.png';
import CryptoJS from 'crypto-js';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import IconButton from '@mui/material/IconButton';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#19438C',
        color: 'white',
        fontSize: 16,
        textAlign: 'center'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: 'center',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
    '&:hover': {
        backgroundColor: '#C1C1C1',
    },
}));

const BuscarIdentidad = () => {
    const [identidad, setIdentidad] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [datos, setDatos] = useState(null);
    const theme = useTheme();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    // Nuevo estado para mostrar el dialog de éxito
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);

    const secretKey = "django-insecure-r*+fidc-a5yhm$*h7%2z5k&oly=!1=)3fyyv$tf-k9=qj%55mv";

    const camposAMostrar = [
        { key: 'DNI', label: 'Identidad' },
        { key: 'nombre', label: 'Nombre Completo' },
        { key: 'fechaNacimiento', label: 'Fecha de Nacimiento' },
        { key: 'sexo', label: 'Sexo' },
        { key: 'estadoBeneficio', label: 'Estado de Beneficio' },
        { key: 'telefonoMovil', label: 'Teléfono Móvil' },
        { key: 'deptoDomicilio', label: 'Departamento de Domicilio' },
        { key: 'estado', label: 'Estado Fe de Vida' },
    ];

    const handleInputChange = (e) => {
        const input = e.target.value;
        if (/^\d{0,13}$/.test(input)) {
            setIdentidad(input);
            setError('');
            setResponseMessage('');
            setDatos(null);
        }
    };

    // Función reutilizable para recargar los datos
    const recargarDatos = async (dni) => {
        setLoading(true);
        try {
            const queryString = `DNI=${dni}`;
            const signature = CryptoJS.HmacSHA256(queryString, secretKey).toString(CryptoJS.enc.Hex);
            const formData = new FormData();
            formData.append('DNI', dni);

            const response = await fetch('https://fevida.injupemp.gob.hn:8443/api/FedeVida/consultar/', {
                method: 'POST',
                headers: { 'X-Signature': signature },
                body: formData,
            });

            const text = await response.text();
            if (response.ok) {
                const data = JSON.parse(text);
                setDatos(data);
            }
        } catch (err) {
            // Opcional: puedes manejar el error aquí si lo deseas
        } finally {
            setLoading(false);
        }
    };

    const handleBuscar = async () => {
        if (!identidad) {
            setError('El número de identidad no puede estar vacío');
            return;
        } else if (identidad.length !== 13) {
            setError('El número de identidad debe tener 13 dígitos');
            return;
        }

        setError('');
        setLoading(true);
        setResponseMessage('');
        setDatos(null);

        try {
            const queryString = `DNI=${identidad}`;
            const signature = CryptoJS.HmacSHA256(queryString, secretKey).toString(CryptoJS.enc.Hex);

            const formData = new FormData();
            formData.append('DNI', identidad);

            const response = await fetch('https://fevida.injupemp.gob.hn:8443/api/FedeVida/consultar/', {
                method: 'POST',
                headers: {
                    'X-Signature': signature,
                },
                body: formData,
            });

            const text = await response.text();
            if (response.ok) {
                const data = JSON.parse(text);
                setDatos(data);
            } else {
                setResponseMessage('Error: ' + text);
            }
        } catch (err) {
            setResponseMessage('Error al conectar con el servidor: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmarIntentos = async () => {
        if (!selectedRow) return;

        const formData = new FormData();
        formData.append('DNI', selectedRow.DNI);
        const signature = CryptoJS.HmacSHA256(`DNI=${selectedRow.DNI}`, secretKey).toString(CryptoJS.enc.Hex);

        try {
            const response = await fetch('https://fevida.injupemp.gob.hn:8443/api/FedeVida/actualizarEstado/', {
                method: 'PATCH',
                headers: { 'X-Signature': signature },
                body: formData,
            });

            const result = await response.text();
            if (response.ok) {
                // Mostrar Dialog de éxito
                setSuccessDialogOpen(true);
                setDialogOpen(false);
            } else {
                alert('Error al habilitar intentos: ' + result);
                setDialogOpen(false);
            }
        } catch (error) {
            alert('Error en la solicitud: ' + error.message);
            setDialogOpen(false);
        }
    };

    // Cuando cierras el dialog de éxito, recarga los datos de la tabla
    const handleCloseSuccessDialog = async () => {
        setSuccessDialogOpen(false);
        if (selectedRow) {
            await recargarDatos(selectedRow.DNI);
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" alignItems="center" mt={4}>
                <Paper elevation={2} sx={{ padding: 4, borderRadius: 4, width: '100%', maxWidth: 600 }}>
                    <Box display="flex" justifyContent="center" alignItems="center">
                        <Box width={100}>
                            <Lottie animationData={corazonAnim} loop />
                        </Box>
                        <img src={logoseparador} alt="Logo" style={{ height: 90 }} />
                    </Box>

                    <Typography variant="h5" fontWeight="bold" marginTop={3} gutterBottom align="center">
                        Activar Intentos - Fe de Vida 2025
                    </Typography>

                    <TextField
                        label="Número de Identidad"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        value={identidad}
                        onChange={handleInputChange}
                        error={!!error}
                        helperText={error}
                        inputProps={{ maxLength: 13, inputMode: 'numeric' }}
                    />

                    <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        size="large"
                        startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                        onClick={handleBuscar}
                        sx={{ marginTop: 2 }}
                        disabled={loading}
                    >
                        {loading ? 'Consultando...' : 'Buscar'}
                    </Button>

                    {responseMessage && (
                        <Alert severity={datos ? 'success' : 'error'} sx={{ marginTop: 2 }}>
                            {responseMessage}
                        </Alert>
                    )}
                </Paper>
            </Box>

            {/* Tabla con encabezado horizontal */}
            {datos && (
                <TableContainer component={Paper} sx={{ marginTop: 4 }}>
                    <Table>
                        <TableHead>
                            <StyledTableRow>
                                {camposAMostrar.map(({ key, label }) => (
                                    <StyledTableCell key={key}>{label}</StyledTableCell>
                                ))}
                                <StyledTableCell>Acciones</StyledTableCell>
                            </StyledTableRow>
                        </TableHead>
                        <TableBody>
                            <StyledTableRow>
                                {camposAMostrar.map(({ key }) => (
                                    <StyledTableCell key={key}>
                                        {key === 'estado' ? (
                                            <Box
                                                sx={{
                                                    backgroundColor: datos[key] === 'Activo' ? 'green' : 'red',
                                                    color: 'white',
                                                    borderRadius: '8px',
                                                    px: 1,
                                                    py: 0.5,
                                                    display: 'inline-block',
                                                    fontWeight: 'bold',
                                                    fontSize: 18
                                                }}
                                            >
                                                {datos[key]}
                                            </Box>
                                        ) : (
                                            datos[key] ?? '—'
                                        )}
                                    </StyledTableCell>
                                ))}
                                <StyledTableCell>
                                    <Box display="flex" justifyContent="center">
                                        <IconButton
                                            onClick={() => {
                                                setSelectedRow(datos);
                                                setDialogOpen(true);
                                            }}
                                            disabled={datos.estado === 'Activo' || datos.estado === 'Inactivo'}
                                            sx={{ width: 48, height: 48 }}
                                        >
                                            <ManageAccountsIcon fontSize="large" />
                                        </IconButton>
                                    </Box>
                                </StyledTableCell>
                            </StyledTableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog de confirmación */}
            {selectedRow && (
                <Dialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    PaperProps={{ sx: { borderRadius: 4, p: 2, minWidth: 400 } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <WarningAmberIcon color="warning" />
                        Confirmar acción
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1">
                            ¿Está seguro de habilitar <strong>2 intentos adicionales</strong> para{' '}
                            <strong>{selectedRow?.nombre}</strong>?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
                        <Button onClick={() => setDialogOpen(false)} color="error" variant="outlined">
                            Cancelar
                        </Button>
                        <Button onClick={handleConfirmarIntentos} color="primary" variant="contained">
                            Confirmar
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Dialog de éxito */}
            <Dialog
                open={successDialogOpen}
                onClose={handleCloseSuccessDialog}
                PaperProps={{ sx: { borderRadius: 4, p: 2, minWidth: 360 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                    <CheckCircleIcon sx={{ color: 'green', fontSize: 32 }} />
                    <Typography variant="h6" fontWeight="bold">
                        Intentos habilitados exitosamente
                    </Typography>
                </DialogTitle>
                <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
                    <Button
                        onClick={handleCloseSuccessDialog}
                        color="success"
                        variant="contained"
                        autoFocus
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default BuscarIdentidad;
